import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Badge, Row, Col, Form, FormGroup, FormLabel, Card, InputGroup, Pagination, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SearchIcon from "../../../images/search-icon.svg";
import Icon1 from "../../../images/transactions/icon-dollar.png";
import Icon2 from "../../../images/transactions/icon-check.png";
import Icon3 from "../../../images/transactions/icon-close.png";
import Icon4 from "../../../images/transactions/icon-dollar_rounded.png";
import { useNavigate } from 'react-router-dom';
import ShortMain from "../../../images/short-icon-main.svg";
import ShortUp from "../../../images/short-icon-top.svg";
import ShortDown from "../../../images/short-icon-bottom.svg";
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import { transactionApi, GetTransactionsParams, Transaction as ApiTransaction } from './TransactionApi.tsx';
import { HiOutlineDownload } from 'react-icons/hi';
import DateRangeSelector from "../../DateRangeSelector";
import ExportModal from "../../ExportModal";
import { exportTransactions, downloadFile, getExportFilename } from '../../../utils/exportUtils';
import "../Support/Support.scss";

type Transaction = ApiTransaction;

interface TransactionStats {
  totalTransactions: number;
  successful: number;
  failed: number;
  refunded: number;
}

interface PaginationInfo {
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

interface SortConfig {
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';
}

interface DateRange {
  startDate: string;
  endDate: string;
}


const Transactions = () => {
  const { withLoader } = useApiLoader();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '',
    sortOrder: ''
  });
  const [transactionStats, setTransactionStats] = useState<TransactionStats>({
    totalTransactions: 0,
    successful: 0,
    failed: 0,
    refunded: 0
  });

  const [selectedCardFilter, setSelectedCardFilter] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });

  const navigate = useNavigate();
  const [itemsPerPage, setItemsPerPage] = useState(10);


  const formatDate = (dateString: any) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return '';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      return '';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date for API (YYYY-MM-DD format)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  const fetchTransactionStats = async () => {
    try {
      setStatsLoading(true);

      const response = await withLoader(
        () => transactionApi.getTransactionStats(),
        'Loading transaction statistics...'
      );

      console.log('🔍 Transaction Stats API Response:', response);

      if (response.status === 'Success' && response.transactionCounts) {
        const stats = {
          totalTransactions: response.transactionCounts.totalTransactions,
          successful: response.transactionCounts.successfulTransactions,
          failed: response.transactionCounts.failedTransactions,
          refunded: response.transactionCounts.refundedTransactions
        };
        console.log('📊 Setting Transaction Stats:', stats);
        setTransactionStats(stats);
      }
    } catch (error: any) {
      console.error('Error fetching transaction stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchTransactions = useCallback(async (
    page = 1,
    search = '',
    status = '',
    limit = itemsPerPage,
    sortBy = '',
    sortOrder = '',
    cardFilter = '',
    typeFilter = '',
    startDate = '',
    endDate = ''
  ) => {
    try {
      setLoading(true);

      const params: GetTransactionsParams = {
        page,
        limit,
        search: search.trim()
      };

      // Handle type filter (for refunds)
      if (typeFilter && typeFilter !== '') {
        params.type = typeFilter;
        console.log('🔍 Using type filter:', typeFilter);
      }
      // Use either status filter or card filter (card filter takes precedence)
      else if (cardFilter && cardFilter !== '') {
        params.status = cardFilter;
        console.log('🔍 Using cardFilter:', cardFilter);
      } else if (status && status !== '') {
        params.status = status;
        console.log('🔍 Using status filter:', status);
      }


      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder as 'asc' | 'desc';
      }

      // Add date range parameters
      if (startDate) {
        params.startDate = formatDateForAPI(startDate);
      }
      if (endDate) {
        params.endDate = formatDateForAPI(endDate);
      }

      console.log('📡 API Params:', params);

      const response = await withLoader(
        () => transactionApi.getAllTransactions(params),
        'Loading transactions...'
      );

      console.log('📥 Transactions API Response:', response);

      if (response.status === 'Success' && response.data) {
        const transactionData = response.data.transactions;
        const paginationData = {
          totalCount: response.data.pagination.totalTransactions,
          totalPages: response.data.pagination.totalPages,
          currentPage: response.data.pagination.currentPage
        };

        console.log(`📊 Loaded ${transactionData.length} transactions`);
        console.log('💾 Transaction data sample:', transactionData.slice(0, 2));

        // Log all unique status values to debug filtering
        const uniqueStatuses = [...new Set(transactionData.map(t => t.status))];
        console.log('🏷️ Unique transaction statuses found:', uniqueStatuses);

        // Log status breakdown for debugging
        const statusCounts = transactionData.reduce((acc, t) => {
          acc[t.status] = (acc[t.status] || 0) + 1;
          return acc;
        }, {});
        console.log('📈 Status breakdown in current page:', statusCounts);

        setTransactions(transactionData);
        setPagination(paginationData);
        setCurrentPage(paginationData.currentPage);

      } else {
        setTransactions([]);
        setPagination({
          totalCount: 0,
          totalPages: 0,
          currentPage: 1
        });
      }
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
      setPagination({
        totalCount: 0,
        totalPages: 0,
        currentPage: 1
      });
    } finally {
      setLoading(false);
    }
  }, [withLoader]);


  const handleCardClick = (cardType: string) => {
    // Prevent clicks on cards with zero values
    const cardValues = {
      'all': transactionStats.totalTransactions,
      'successful': transactionStats.successful,
      'failed': transactionStats.failed,
      'refunded': transactionStats.refunded
    };

    if (cardValues[cardType as keyof typeof cardValues] === 0) {
      console.log(`🚫 Card click prevented: ${cardType} has zero value`);
      return;
    }

    let statusFilter = '';
    let typeFilterValue = '';

    switch (cardType) {
      case 'successful':
        statusFilter = 'Paid';
        break;
      case 'failed':
        statusFilter = 'Failed';
        break;
      case 'refunded':
        typeFilterValue = 'refund';
        statusFilter = '';
        break;
      case 'all':
      default:
        statusFilter = '';
        typeFilterValue = '';
        break;
    }

    console.log(`🎯 Card clicked: ${cardType}, Status Filter: '${statusFilter}', Type Filter: '${typeFilterValue}'`);

    setSelectedCardFilter(cardType === 'refunded' ? 'Refunded' : statusFilter);
    setCurrentPage(1);

    // Handle refunds with type filter instead of status filter
    if (cardType === 'refunded') {
      setStatusFilter('');
      fetchTransactions(1, searchTerm, '', itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, '', 'refund', dateRange.startDate, dateRange.endDate);
    } else {
      fetchTransactions(1, searchTerm, statusFilter, itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, statusFilter, '', dateRange.startDate, dateRange.endDate);
    }
  };

  const handleSort = (field: string) => {
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (sortConfig.sortBy === field) {
      if (sortConfig.sortOrder === 'asc') {
        newSortOrder = 'desc';
      } else if (sortConfig.sortOrder === 'desc') {
        setSortConfig({ sortBy: '', sortOrder: '' });
        fetchTransactions(currentPage, searchTerm, statusFilter, itemsPerPage, '', '', selectedCardFilter, selectedCardFilter === 'Refunded' ? 'refund' : '', dateRange.startDate, dateRange.endDate);
        return;
      }
    }

    setSortConfig({ sortBy: field, sortOrder: newSortOrder });
    fetchTransactions(currentPage, searchTerm, statusFilter, itemsPerPage, field, newSortOrder, selectedCardFilter, selectedCardFilter === 'Refunded' ? 'refund' : '', dateRange.startDate, dateRange.endDate);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.sortBy !== field) {
      return (
        <span className='short-icon'>
          <img src={ShortMain} alt='' />
        </span>
      );
    }

    if (sortConfig.sortOrder === 'asc') {
      return (
        <span className='short-icon'>
          <img src={ShortUp} alt='' />
        </span>
      );
    } else {
      return (
        <span className='short-icon'>
          <img src={ShortDown} alt='' />
        </span>
      );
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages && page !== currentPage) {
      setCurrentPage(page);
      fetchTransactions(page, searchTerm, statusFilter, itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, selectedCardFilter === 'Refunded' ? 'refund' : '', dateRange.startDate, dateRange.endDate);
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setSelectedCardFilter(value);
    setCurrentPage(1);

    fetchTransactions(
      1,
      searchTerm,
      value === 'Refunded' ? '' : value,
      itemsPerPage,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      value,
      value === 'Refunded' ? 'refund' : '',
      dateRange.startDate,
      dateRange.endDate
    );
  };

  // Handle date range change
  const handleDateRangeChange = useCallback((dateRangeString: string, isCustom?: boolean) => {
    // Parse the date range string "MM/DD/YYYY - MM/DD/YYYY"
    if (!dateRangeString || dateRangeString.trim() === '') {
      // Clear the filter
      setDateRange({ startDate: '', endDate: '' });
      setCurrentPage(1);
      fetchTransactions(
        1,
        searchTerm,
        statusFilter,
        itemsPerPage,
        sortConfig.sortBy,
        sortConfig.sortOrder,
        selectedCardFilter,
        selectedCardFilter === 'Refunded' ? 'refund' : '',
        '',
        ''
      );
      return;
    }

    // Split the date range string
    const dates = dateRangeString.split(' - ');
    if (dates.length === 2) {
      const startDateStr = dates[0].trim();
      const endDateStr = dates[1].trim();

      // Convert MM/DD/YYYY to YYYY-MM-DD without timezone conversion
      const parseDate = (dateStr: string): string => {
        const [month, day, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const formattedStartDate = parseDate(startDateStr);
      const formattedEndDate = parseDate(endDateStr);

      // Validate dates
      if (formattedStartDate > formattedEndDate) {
        toast.error('Start date cannot be after end date');
        return;
      }

      setDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });
      setCurrentPage(1);

      fetchTransactions(
        1,
        searchTerm,
        statusFilter,
        itemsPerPage,
        sortConfig.sortBy,
        sortConfig.sortOrder,
        selectedCardFilter,
        selectedCardFilter === 'Refunded' ? 'refund' : '',
        formattedStartDate,
        formattedEndDate
      );
    }
  }, [searchTerm, statusFilter, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, fetchTransactions]);

  // Handle export
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Preparing Excel export...');
      const blob = await exportTransactions('excel', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Transactions', 'excel', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('Excel file downloaded successfully!');
    } catch (error: any) {
      console.error('Export Excel error:', error);
      if (error.response?.status === 404) {
        toast.error('No transactions found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export Excel file. Please try again.');
      }
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info('Preparing PDF export...');
      const blob = await exportTransactions('pdf', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Transactions', 'pdf', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('PDF file downloaded successfully!');
    } catch (error: any) {
      console.error('Export PDF error:', error);
      if (error.response?.status === 404) {
        toast.error('No transactions found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export PDF file. Please try again.');
      }
    }
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchTransactions(1, searchTerm, statusFilter, itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, selectedCardFilter === 'Refunded' ? 'refund' : '', dateRange.startDate, dateRange.endDate);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchTransactions(1, '', '', itemsPerPage, '', '', '', '', '', '');
    fetchTransactionStats();
  }, []);

  // Re-fetch when items per page changes
  useEffect(() => {
    setCurrentPage(1);
    fetchTransactions(1, searchTerm, statusFilter, itemsPerPage, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, selectedCardFilter === 'Refunded' ? 'refund' : '', dateRange.startDate, dateRange.endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage]);

  const getStatusBadge = (status: string) => {
    let badgeClass = '';
    let displayText = status;

    switch (status?.toLowerCase()) {
      case 'paid':
      case 'success':
      case 'successful':
        badgeClass = 'succ-badge';
        displayText = 'Success';
        break;
      case 'completed':
        badgeClass = 'gre-badge';
        displayText = 'Completed';
        break;
      case 'failed':
        badgeClass = 'gre-badge';
        displayText = 'Failed';
        break;
      case 'refunded':
        badgeClass = 'blu-badge';
        displayText = 'Refunded';
        break;
      case 'pending':
        badgeClass = 'or-badge';
        displayText = 'Pending';
        break;
      default:
        badgeClass = 'secondary';
        displayText = status;
    }
    return <Badge bg={badgeClass}>{displayText}</Badge>;
  };

  const data = [
    {
      icon: Icon1,
      value: transactionStats.totalTransactions,
      label: "Total Transactions",
      colorClass: "blue-text",
      onClick: () => {
        console.log('🔗 Total Transactions clicked, value:', transactionStats.totalTransactions);
        handleCardClick('all');
      },
      isActive: selectedCardFilter === '',
      isClickable: transactionStats.totalTransactions > 0
    },
    {
      icon: Icon2,
      value: transactionStats.successful,
      label: "Successful",
      colorClass: "green-text",
      onClick: () => {
        console.log('🔗 Successful clicked, value:', transactionStats.successful);
        handleCardClick('successful');
      },
      isActive: selectedCardFilter === 'Paid',
      isClickable: transactionStats.successful > 0
    },
    {
      icon: Icon3,
      value: transactionStats.failed,
      label: "Failed",
      colorClass: "orange-text",
      onClick: () => {
        console.log('🔗 Failed clicked, value:', transactionStats.failed);
        handleCardClick('failed');
      },
      isActive: selectedCardFilter === 'Failed',
      isClickable: transactionStats.failed > 0
    },
    {
      icon: Icon4,
      value: transactionStats.refunded,
      label: "Refunded",
      colorClass: "blue-text",
      onClick: () => {
        console.log('🔗 Refunded clicked, value:', transactionStats.refunded);
        handleCardClick('refunded');
      },
      isActive: selectedCardFilter === 'Refunded',
      isClickable: transactionStats.refunded > 0
    },
  ];

  return (
    <>
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
          <div className="icon-grid-sec">
            <ul className='icon-grid mb-4'>
              <div className='row-4 m-0'>
                {data.map((item, idx) => (
                  <li
                    className={`icon-block ${item.isActive ? 'active-card' : ''} ${!item.isClickable ? 'non-clickable' : ''}`}
                    key={idx}
                    onClick={item.isClickable ? item.onClick : undefined}
                    style={{ cursor: item.isClickable ? 'pointer' : 'not-allowed', opacity: item.isClickable ? 1 : 0.6 }}
                  >
                    <div className="icon-block-in d-flex justify-content-between align-items-center">
                      <figure><img src={item.icon} alt="" /></figure>
                      <div className="icon-grid-cont">
                        <h2 className={item.colorClass}>
                          {statsLoading ? '...' : item.value}
                        </h2>
                        <p>{item.label}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </div>
              <div className="clearfix"></div>
            </ul>
          </div>

          <div className="management-sec">
            <div className="management-sec-in">
              <div className='main-table'>
                <div className="tab-content tab-body">
                  <div className="referral-discount mb-3">
                    {/* Filter and Search Row */}
                    <div className="mb-3 main-table-filter">
                      <div className="row g-2 align-items-stretch">
                        {/* Search Field */}
                        <div className='col-lg-3 col-md-6 col-12'>
                          <div className='main-table-search h-100'>
                            <FormGroup className='form-group search-box mb-0 h-100'>
                              <InputGroup className="h-100">
                                <Form.Control
                                  type="text"
                                  placeholder="Search transactions..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  className="h-100"
                                />
                                <InputGroup.Text>
                                  <img src={SearchIcon} alt='' />
                                </InputGroup.Text>
                              </InputGroup>
                            </FormGroup>
                          </div>
                        </div>

                        {/* Filter Dropdown */}
                        <div className='col-lg-2 col-md-6 col-12'>
                          <div className='select-field h-100'>
                            <FormGroup className='form-group mb-0 h-100'>
                              <Form.Select
                                value={statusFilter}
                                onChange={(e) => handleStatusFilterChange(e.target.value)}
                                className="h-100"
                              >
                                <option value="">Filter by Status</option>
                                <option value="Paid">Paid</option>
                                <option value="Failed">Failed</option>
                                <option value="Refunded">Refunded</option>
                              </Form.Select>
                            </FormGroup>
                          </div>
                        </div>

                        {/* Spacer to push date range and export to the right */}
                        <div className='col-lg-3 d-none d-lg-block'></div>

                        {/* Date Range Picker */}
                        <div className='col-lg-3 col-md-9 col-12'>
                          <div className='calender-block h-100'>
                            <DateRangeSelector
                              onDateRangeChange={handleDateRangeChange}
                            />
                          </div>
                        </div>

                        {/* Export Button */}
                        <div className='col-lg-1 col-md-3 col-12'>
                          <Button
                            variant="primary"
                            onClick={handleExport}
                            className="d-flex align-items-center justify-content-center gap-2 h-100 w-100"
                          >
                            <HiOutlineDownload size={18} />
                            <span>Export</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive mt-3">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Transaction ID</th>
                    <th>Transaction Date</th>
                    <th>Customer Name</th>
                    <th>Type</th>
                    <th>Order ID</th>
                    <th>Payment Method</th>
                    <th>Amount</th>
                    <th>Discount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2">Loading transactions...</span>
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <tr
                        key={transaction.transactionId}
                        onClick={() => {
                          // Store full transaction ID in sessionStorage
                          sessionStorage.setItem('currentTransactionId', transaction.transactionId);
                          // Navigate with last 5 digits in URL for privacy
                          navigate(`/transactions/${transaction.transactionId}/${transaction.type.toLowerCase()}`);
                        }}
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        <td>{transaction.transactionId8Digit || transaction.transactionId}</td>
                        <td>{formatDate(transaction.date)}</td>
                        <td>{transaction.customerName}</td>
                        <td>{transaction.type}</td>
                        <td>{transaction.details.orderId}</td>
                        <td>{transaction.paymentMethod}</td>
                        <td>{formatCurrency(transaction.amountPaid)}</td>
                        <td>{transaction.details.discountPercentage ? `${transaction.details.discountPercentage}%` : 'N/A'}</td>
                        <td className="info-badge-area">
                          <p className="d-flex align-items-center gap-1 mb-0">
                            {getStatusBadge(transaction.status)}
                          </p>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        <p className="text-muted mb-0">
                          {searchTerm
                            ? 'No transactions found matching your search criteria.'
                            : 'No transactions found.'
                          }
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {!loading && transactions.length > 0 && pagination.totalPages > 1 && (
              <div className="pagination-wrapper mt-3">
                <div className="pagination-info-row">
                  <div className="text-muted">
                    Showing {((pagination.currentPage - 1) * itemsPerPage) + 1} to {Math.min(pagination.currentPage * itemsPerPage, pagination.totalCount)} of {pagination.totalCount} entries
                    {(dateRange.startDate || dateRange.endDate) && (
                      <span className="ms-2">(filtered by date range)</span>
                    )}
                  </div>
                </div>
                <div className="pagination-controls-row d-flex align-items-center gap-3">
                  <div className="pagination-per-page d-flex align-items-center gap-2">
                    <span className="pagination-label">Show</span>
                    <select
                      className="form-select form-select-sm pagination-select"
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <Pagination>
                    <Pagination.Prev
                      disabled={pagination.currentPage === 1 || loading}
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                    />

                    {pagination.currentPage > 3 && (
                      <>
                        <Pagination.Item
                          onClick={() => handlePageChange(1)}
                          disabled={loading}
                        >
                          1
                        </Pagination.Item>
                        {pagination.currentPage > 4 && (
                          <Pagination.Ellipsis disabled />
                        )}
                      </>
                    )}

                    {Array.from({ length: pagination.totalPages }, (_, index) => index + 1)
                      .filter(page =>
                        page >= Math.max(1, pagination.currentPage - 2) &&
                        page <= Math.min(pagination.totalPages, pagination.currentPage + 2)
                      )
                      .map(page => (
                        <Pagination.Item
                          key={page}
                          active={page === pagination.currentPage}
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                        >
                          {page}
                        </Pagination.Item>
                      ))
                    }

                    {pagination.currentPage < pagination.totalPages - 2 && (
                      <>
                        {pagination.currentPage < pagination.totalPages - 3 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          onClick={() => handlePageChange(pagination.totalPages)}
                          disabled={loading}
                        >
                          {pagination.totalPages}
                        </Pagination.Item>
                      </>
                    )}

                    <Pagination.Next
                      disabled={pagination.currentPage === pagination.totalPages || loading}
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </div>
            )}
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Container>
      </section>

      {/* Export Modal */}
      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        title="Export Transactions Data"
      />
    </>
  );
};

export default Transactions;